"use client";

import type { AICheckRunResult } from "@/types/aicheck";

interface ResultsSummaryProps {
  result: AICheckRunResult;
  isDismissed?: (id: string) => boolean;
}

export function ResultsSummary({ result, isDismissed }: ResultsSummaryProps) {
  const dismissed = isDismissed ?? (() => false);

  let activePassed = 0;
  let activeWarning = 0;
  let activeFailed = 0;
  let dismissedPassed = 0;
  let dismissedWarning = 0;
  let dismissedFailed = 0;

  for (const r of result.aiResults) {
    const isDis = dismissed(r.checkId);
    if (r.status === "pass") {
      if (isDis) dismissedPassed++;
      else activePassed++;
    } else if (r.status === "warning") {
      if (isDis) dismissedWarning++;
      else activeWarning++;
    } else if (r.status === "fail" || r.status === "error") {
      if (isDis) dismissedFailed++;
      else activeFailed++;
    }
  }

  for (const r of result.consistencyResults) {
    const isDis = dismissed(r.ruleId);
    if (r.passed) {
      if (isDis) dismissedPassed++;
      else activePassed++;
    } else if (r.severity === "error" || r.evaluationError) {
      if (isDis) dismissedFailed++;
      else activeFailed++;
    } else {
      if (isDis) dismissedWarning++;
      else activeWarning++;
    }
  }

  return (
    <div className="grid grid-cols-3 gap-3 mb-6">
      <SummaryCard
        count={activePassed}
        dismissedCount={dismissedPassed}
        label={activePassed === 1 ? "Pass" : "Passes"}
        borderColor="border-green-200"
        bgColor="bg-green-50"
        textColor="text-green-700"
        labelColor="text-green-600"
      />
      <SummaryCard
        count={activeWarning}
        dismissedCount={dismissedWarning}
        label={activeWarning === 1 ? "Warning" : "Warnings"}
        borderColor="border-yellow-200"
        bgColor="bg-yellow-50"
        textColor="text-yellow-700"
        labelColor="text-yellow-600"
      />
      <SummaryCard
        count={activeFailed}
        dismissedCount={dismissedFailed}
        label={activeFailed === 1 ? "Fail" : "Fails"}
        borderColor="border-red-200"
        bgColor="bg-red-50"
        textColor="text-red-700"
        labelColor="text-red-600"
      />
    </div>
  );
}

function SummaryCard({
  count,
  dismissedCount,
  label,
  borderColor,
  bgColor,
  textColor,
  labelColor,
}: {
  count: number;
  dismissedCount: number;
  label: string;
  borderColor: string;
  bgColor: string;
  textColor: string;
  labelColor: string;
}) {
  return (
    <div className={`rounded-lg border ${borderColor} ${bgColor} p-4`}>
      <div className={`text-2xl font-bold ${textColor}`}>
        {count}
        {dismissedCount > 0 && (
          <span className="text-sm font-normal text-gray-400 ml-1">
            (+{dismissedCount} dissmissed)
          </span>
        )}
      </div>
      <div className={`text-xs font-medium ${labelColor} mt-1`}>{label}</div>
    </div>
  );
}
